import React from "react";
import { ImportSteps, ValidationRow, SummaryStat } from "../../../../shared/components/ImportSteps";

interface ImportStepsProviderProps {
    currentStep: "file" | "validation" | "confirmation";
    selectedFile: File | null;
    importMode: "flush" | "update" | null;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onImportModeChange: (mode: "flush" | "update") => void;
    onNext: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    isUploading: boolean;
    hasValidationErrors: boolean;
    validatedRows: ValidationRow[];
    onBack: () => void;
    onImport: () => void;
    originalRowCount?: number;
}

export const ImportStepsProvider = (props: ImportStepsProviderProps) => {
    const { originalRowCount, validatedRows, ...restProps } = props;
    
    // Calculate custom summary stats for providers (accounting for deduplication)
    const validRows = validatedRows.filter((r) => r.isValid);
    const invalidRows = validatedRows.filter((r) => !r.isValid);
    const uniqueProvidersCount = validatedRows.length;
    const totalRowsProcessed = originalRowCount || validatedRows.length;
    const duplicatesCount = totalRowsProcessed - uniqueProvidersCount;
    
    // Count all errors (including warnings from duplicates)
    const totalErrorsCount = validatedRows.reduce((sum, row) => sum + (row.errors?.length || 0), 0);
    
    const summaryStats: SummaryStat[] = [
        {
            value: uniqueProvidersCount,
            label: "Nhà cung cấp",
            backgroundColor: "#e8f5e9",
            borderColor: "#4caf50",
            textColor: "#2e7d32",
        },
        {
            value: totalErrorsCount,
            label: "Lỗi",
            backgroundColor: totalErrorsCount > 0 ? "#ffcdd2" : "#e8f5e9",
            borderColor: totalErrorsCount > 0 ? "#f44336" : "#4caf50",
            textColor: totalErrorsCount > 0 ? "#c62828" : "#2e7d32",
        },
        {
            value: duplicatesCount,
            label: "Dòng đã gộp",
            backgroundColor: duplicatesCount > 0 ? "#fff3e0" : "#e8f5e9",
            borderColor: duplicatesCount > 0 ? "#ff9800" : "#4caf50",
            textColor: duplicatesCount > 0 ? "#e65100" : "#2e7d32",
        },
        {
            value: totalRowsProcessed,
            label: "Tổng số dòng",
            backgroundColor: "#e3f2fd",
            borderColor: "#2196f3",
            textColor: "#1565c0",
        },
    ];
    
    const fileFormatInstructions = (
        <>
            <div style={{ fontWeight: 500, marginBottom: "4px" }}>Lưu ý về định dạng file:</div>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
                <li>Headers phải ở dòng 3</li>
                <li>Dữ liệu bắt đầu từ dòng 4</li>
                <li>Trường bắt buộc: name (Tên nhà cung cấp)</li>
                <li>
                    Các trường tùy chọn: provider_code, phone, tax_id, fax, email, address, note,
                    payable_days, is_internal, bank_account_name, bank_name, bank_account_number
                </li>
                <li>
                    Để nhập thông tin liên hệ, sử dụng tiền tố: contact_list.name, contact_list.email,
                    contact_list.phone, contact_list.position
                </li>
            </ul>
        </>
    );

    return (
        <ImportSteps
            {...restProps}
            validatedRows={validatedRows}
            title="Nhập danh sách nhà cung cấp"
            summaryStats={summaryStats}
            templateUrl="/templates/provider_import.xlsx"
            importModeLabels={{
                update: {
                    title: "Cập nhật và thêm mới",
                    description: "Nhà cung cấp mới sẽ được thêm vào. Nhà cung cấp trùng mã sẽ được cập nhật với dữ liệu từ file.",
                },
                flush: {
                    title: "Xóa tất cả và nhập mới",
                    description: "Tất cả nhà cung cấp hiện có sẽ bị xóa vĩnh viễn và thay thế bằng dữ liệu từ file. Hành động này không thể hoàn tác.",
                    warning: "CẢNH BÁO",
                },
            }}
            fileFormatInstructions={fileFormatInstructions}
            previewColumns={[
                { key: "name", label: "Tên NCC" },
                { key: "provider_code", label: "Mã NCC" },
                { key: "phone", label: "SĐT" },
                { key: "email", label: "Email" },
                { key: "tax_id", label: "Mã số thuế" },
                { key: "fax", label: "Fax" },
                { key: "address", label: "Địa chỉ" },
                { key: "payable_days", label: "Số ngày nợ" },
                { key: "is_internal", label: "Nội bộ" },
                { key: "bank_account_name", label: "Tên TK" },
                { key: "bank_name", label: "Ngân hàng" },
                { key: "bank_account_number", label: "Số TK" },
                { key: "contact_list", label: "Người liên hệ", isNested: true },
            ]}
        />
    );
};

